import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Package,
  RotateCcw,
  Share2,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react'
import { api } from '@/lib/api'
import { haptic } from '@/lib/telegram'
import { localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { usePageTitle } from '@/lib/usePageTitle'
import {
  buildFragranceProfile,
  buildShareText,
  decodeQuizState,
  encodeQuizState,
} from '@/lib/quizProfile'
import type { QuizMatchResult, QuizStep } from '@/shared/types'

interface QuizConfigResponse {
  steps: QuizStep[]
}

interface PublicSettings {
  shop_name: string
  currency_rate?: number
}

function fillTemplate(tpl: string, vars: Record<string, string | number>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? ''))
}

export function QuizPage() {
  const lang = useAppStore((s) => s.lang)
  const T = useT('quizNav')

  const [stepIndex, setStepIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string[]>>(() => {
    if (typeof window === 'undefined') return {}
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const stateParam = searchParams.get('state') || searchParams.get('answers')
      if (stateParam) {
        const restored = decodeQuizState(stateParam)
        if (restored && Object.keys(restored).length > 0) {
          return restored
        }
      }
    } catch {
      // Ignore URL parsing errors
    }
    return {}
  })

  const [showResults, setShowResults] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const stateParam = searchParams.get('state') || searchParams.get('answers')
      if (stateParam) {
        const restored = decodeQuizState(stateParam)
        return Boolean(restored && Object.keys(restored).length > 0)
      }
    } catch {
      // Ignore URL parsing errors
    }
    return false
  })

  const [copied, setCopied] = useState(false)

  const { data: config } = useQuery<QuizConfigResponse>({
    queryKey: ['quiz-config'],
    queryFn: () => api.get<QuizConfigResponse>('/quiz/config'),
    staleTime: 300_000,
  })
  const steps = useMemo(() => config?.steps ?? [], [config])
  const step = steps[stepIndex]

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const currencyRate = settings?.currency_rate || 1

  usePageTitle(settings?.shop_name ? `${T.title} — ${settings.shop_name}` : T.title)

  const [match, setMatch] = useState<QuizMatchResult | null>(null)
  useEffect(() => {
    if (steps.length === 0) return
    let cancelled = false
    api.post<QuizMatchResult>('/quiz/match', { answers }).then((res) => {
      if (!cancelled) setMatch(res)
    })
    return () => {
      cancelled = true
    }
  }, [answers, steps.length])

  // Compute Olfactory Profile
  const profile = useMemo(() => {
    return buildFragranceProfile(answers, steps, lang)
  }, [answers, steps, lang])

  // Sync share state to URL query string when viewing results
  useEffect(() => {
    if (!showResults || Object.keys(answers).length === 0) return
    const encoded = encodeQuizState(answers)
    if (!encoded) return
    const url = new URL(window.location.href)
    if (url.searchParams.get('state') !== encoded) {
      url.searchParams.set('state', encoded)
      window.history.replaceState(null, '', url.toString())
    }
  }, [showResults, answers])

  if (!config) return null

  if (steps.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#3a4352] px-6 py-12 text-center text-[#b9aa99]">
        <p className="text-lg">{T.noMatches}</p>
        <Link
          to="/catalog"
          className="rounded-2xl bg-[#bc9050] px-6 py-2.5 text-sm font-bold text-[#17120f] transition hover:bg-[#bc9050]/90 active:scale-95"
        >
          {T.viewResults}
        </Link>
      </div>
    )
  }

  const toggleOption = (optionVal: string) => {
    haptic('light')
    setAnswers((prev) => {
      const current = prev[step.id] ?? []
      if (step.mode === 'single') {
        if (current.includes(optionVal)) {
          return { ...prev, [step.id]: [] }
        } else {
          return { ...prev, [step.id]: [optionVal] }
        }
      } else {
        const next = current.includes(optionVal)
          ? current.filter((o) => o !== optionVal)
          : [...current, optionVal]
        if (step.limit && next.length > step.limit) {
          return prev
        }
        return { ...prev, [step.id]: next }
      }
    })
  }

  const restart = () => {
    haptic('light')
    setAnswers({})
    setStepIndex(0)
    setShowResults(false)
    const url = new URL(window.location.href)
    url.searchParams.delete('state')
    url.searchParams.delete('answers')
    window.history.replaceState(null, '', url.pathname)
  }

  const tweak = () => {
    haptic('light')
    setShowResults(false)
    setStepIndex(0)
  }

  const getShareUrl = (): string => {
    const encoded = encodeQuizState(answers)
    const origin = window.location.origin
    return `${origin}/app/quiz?state=${encoded}`
  }

  const handleCopyProfile = async () => {
    haptic('medium')
    const shareUrl = getShareUrl()
    const text = buildShareText(profile, match?.products ?? [], shareUrl, lang)
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
      } else if (typeof document !== 'undefined') {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try {
          document.execCommand?.('copy')
        } catch {
          // ignore
        }
        if (textArea.parentNode) {
          document.body.removeChild(textArea)
        }
      }
    } catch {
      // Fallback
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  const handleShare = async () => {
    haptic('medium')
    const shareUrl = getShareUrl()
    const text = buildShareText(profile, match?.products ?? [], shareUrl, lang)
    if (navigator.share) {
      try {
        await navigator.share({
          title: profile.archetypeTitle,
          text: text,
          url: shareUrl,
        })
        return
      } catch {
        // User cancelled or unsupported, proceed to Telegram fallback
      }
    }
    // Telegram share fallback
    const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(
      `✨ ${profile.archetypeTitle}\n${profile.tagline}`
    )}`
    window.open(tgUrl, '_blank')
  }

  if (showResults) {
    return (
      <div className="min-h-screen bg-[#3a4352] px-4 pb-28 pt-6 text-[#f8f3ea] md:px-8">
        {/* Top Header */}
        <div className="mb-6 flex items-center justify-between border-b border-[#6f5840]/30 pb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white">{T.results}</h1>
            <p className="text-xs text-[#b9aa99]">
              {fillTemplate(T.foundCount, { count: match?.total ?? match?.products.length ?? 0 })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={tweak}
              className="flex items-center gap-1.5 rounded-xl border border-[#6f5840]/40 bg-[#2d333e] px-3 py-1.5 text-xs font-semibold text-[#f8f3ea] transition hover:border-[#bc9050]"
            >
              <SlidersHorizontal className="h-3.5 w-3.5 text-[#bc9050]" />
              {T.tweakAnswers}
            </button>
            <button
              onClick={restart}
              className="flex items-center gap-1.5 rounded-xl border border-[#6f5840]/40 bg-[#2d333e] px-3 py-1.5 text-xs font-semibold text-[#b9aa99] transition hover:text-white"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {T.startOver}
            </button>
          </div>
        </div>

        {/* Olfactory Profile Card */}
        <div className="mb-8 overflow-hidden rounded-2xl border border-[#bc9050]/40 bg-gradient-to-br from-[#2d333e] via-[#353d4c] to-[#252b35] p-5 shadow-2xl">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#bc9050]/20 pb-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#bc9050]/20 text-[#d7b171] ring-1 ring-[#bc9050]/40">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#d7b171]">
                  {T.profileTitle}
                </span>
                <h2 className="text-lg font-bold leading-snug text-white md:text-xl">
                  «{profile.archetypeTitle}»
                </h2>
              </div>
            </div>

            {/* Quick Actions (Copy & Share) */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyProfile}
                className={`flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 text-xs font-bold transition active:scale-95 ${
                  copied
                    ? 'bg-emerald-600 text-white'
                    : 'bg-[#bc9050] text-[#17120f] hover:bg-[#bc9050]/90'
                }`}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? T.copied : T.copyProfile}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 rounded-xl border border-[#bc9050]/40 bg-[#1f242d] px-3.5 py-1.5 text-xs font-bold text-[#f8f3ea] transition hover:border-[#bc9050] active:scale-95"
              >
                <Share2 className="h-3.5 w-3.5 text-[#d7b171]" />
                {T.shareProfile}
              </button>
            </div>
          </div>

          {/* Subtitle / Tagline */}
          <p className="mt-3.5 text-sm font-medium italic text-[#d7b171]">{profile.tagline}</p>

          {/* Narrative Text */}
          <p className="mt-2.5 text-sm leading-relaxed text-[#e0d6c8]">{profile.narrative}</p>

          {/* Key Profile Facet Badges */}
          {profile.facets.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 pt-3 border-t border-[#6f5840]/20">
              {profile.facets.map((facet, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-[#6f5840]/30 bg-[#252b35]/70 p-2.5"
                >
                  <p className="text-[11px] font-semibold text-[#b9aa99] flex items-center gap-1">
                    <span>{facet.icon}</span>
                    <span>{facet.label}</span>
                  </p>
                  <p className="mt-0.5 text-xs font-bold text-white line-clamp-1">{facet.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section Title for Products */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">{T.whyMatch}</h2>
        </div>

        {match && match.products.length === 0 && (
          <div className="rounded-2xl border border-[#6f5840]/20 bg-[#2d333e] p-12 text-center text-[#b9aa99]">
            <p className="text-base">{T.noMatches}</p>
            <button
              onClick={tweak}
              className="mt-4 inline-flex items-center gap-1.5 rounded-2xl bg-[#bc9050] px-5 py-2 text-sm font-bold text-[#17120f] transition hover:bg-[#bc9050]/90"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {T.tweakAnswers}
            </button>
          </div>
        )}

        {/* Product Recommendations Grid */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {(match?.products ?? []).map((product) => {
            const name = localizedName(lang, product.name_uk, product.name_en)
            const priceUah = Math.round(product.price_usd * currencyRate)
            return (
              <Link
                key={product.id}
                to="/catalog/$productSlug"
                params={{ productSlug: product.slug || String(product.id) }}
                className="group flex flex-col overflow-hidden rounded-xl border border-[#657086]/60 bg-[#f8f3ea] text-[#211915] shadow-lg transition duration-200 hover:border-[#bc9050] hover:shadow-[0_4px_20px_rgba(188,144,80,0.2)]"
              >
                <div className="relative aspect-square w-full overflow-hidden bg-[#efe3d1]">
                  {product.photo_url ? (
                    <img
                      src={product.photo_url}
                      alt={name}
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Package className="h-8 w-8 text-[#9d733a]" />
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 rounded-md bg-[#211915]/80 px-2 py-0.5 text-[10px] font-bold text-[#d7b171] backdrop-blur-sm">
                    {T.matchReason}
                  </div>
                </div>
                <div className="flex flex-1 flex-col justify-between p-3.5">
                  <p className="line-clamp-2 min-h-[38px] text-sm font-semibold leading-tight text-[#211915]">
                    {name}
                  </p>
                  <div className="mt-2.5 flex items-center justify-between pt-2 border-t border-[#bc9050]/20">
                    <p className="text-sm font-bold text-[#9d733a]">{priceUah} UAH</p>
                    <span className="text-[11px] font-medium text-[#7c6245] group-hover:text-[#bc9050]">
                      →
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    )
  }

  const selected = answers[step.id] ?? []
  const isLast = stepIndex === steps.length - 1

  return (
    <div className="flex min-h-screen flex-col bg-[#3a4352] px-5 pb-28 pt-6 text-[#f8f3ea]">
      <div className="mb-6">
        <div className="h-2 w-full overflow-hidden rounded-full bg-[#efe3d1]/10">
          <div
            className="h-full rounded-full bg-[#bc9050] transition-all duration-300"
            style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-[#b9aa99]">
          <p>{fillTemplate(T.step, { current: stepIndex + 1, total: steps.length })}</p>
          {step.mode === 'multi' && (
            <p className="text-[#d7b171]">
              {step.limit
                ? `Оберіть до ${step.limit} варіантів`
                : 'Оберіть кілька варіантів'}
            </p>
          )}
        </div>
      </div>

      {step.image && (
        <div className="mb-5 overflow-hidden border border-[#6f5840]/20 h-32 md:h-44 w-full relative rounded-2xl">
          <img src={step.image} alt={step.title} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#3a4352] via-transparent to-transparent" />
        </div>
      )}

      <h1 className="mb-2 text-2xl font-semibold text-white">{step.title}</h1>
      {step.subtitle && <p className="mb-6 text-sm text-[#b9aa99]">{step.subtitle}</p>}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {step.options.map((option) => {
          const active = selected.includes(option.value)
          return (
            <button
              key={option.value}
              onClick={() => toggleOption(option.value)}
              className={`group flex flex-col overflow-hidden rounded-xl border transition-all duration-300 active:scale-[0.98] ${
                active
                  ? 'border-[#bc9050] shadow-[0_0_12px_rgba(188,144,80,0.3)] ring-2 ring-[#bc9050]'
                  : 'border-[#6f5840]/30 hover:border-[#bc9050]/50'
              }`}
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img
                  src={option.image}
                  alt={option.label}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {active && (
                  <div className="absolute right-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-[#bc9050] text-[#17120f]">
                    <svg className="h-3 w-3 fill-none stroke-current stroke-[3]" viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
              </div>

              <div
                className={`px-2 py-2.5 text-center transition-colors ${
                  active ? 'bg-[#bc9050]/15' : 'bg-[#2d333e]'
                }`}
              >
                <span
                  className={`line-clamp-2 block text-xs font-semibold uppercase leading-tight tracking-wider transition-colors ${
                    active ? 'text-[#d7b171]' : 'text-[#f8f3ea]'
                  }`}
                >
                  {option.label}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      <div className="mt-auto flex items-center justify-between pt-8 border-t border-[#6f5840]/20">
        <button
          disabled={stepIndex === 0}
          onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
          className="flex items-center gap-1 text-sm text-[#b9aa99] hover:text-white disabled:opacity-0 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {T.back}
        </button>

        <p className="text-sm font-semibold text-[#d7b171]">
          {fillTemplate(T.foundCount, { count: match?.total ?? '…' })}
        </p>

        <button
          onClick={() => {
            haptic('medium')
            if (isLast) {
              setShowResults(true)
            } else {
              setStepIndex((i) => i + 1)
            }
          }}
          className="flex items-center gap-1.5 rounded-2xl bg-[#bc9050] text-[#17120f] font-bold px-6 py-2.5 text-sm transition-all hover:bg-[#bc9050]/90 active:scale-95"
        >
          {isLast ? T.viewResults : T.next}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
